﻿import L from "leaflet";
import "leaflet-draw";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { getPermitIcon } from "../ts/icons";
import { PermitType, PermitCategory, getHighestPermitCategory } from "../ts/common/enums";
import { getMarkerZIndex } from "../ts/common/mapUtils";

export function enable() {
    const shouldImportDraw = Boolean(document.getElementById("can-draw"));

    const map = L.map("map", { attributionControl: false }).setView([57.688111, 11.848859], 14);
    const positionPolygonLayers = new L.FeatureGroup();
    const polygonLayers = new L.FeatureGroup();
    const markerLayers = new L.FeatureGroup();
    const pumpingLayers = new L.FeatureGroup();

    delete L.Icon.Default.prototype._getIconUrl;

    L.Icon.Default.mergeOptions({
        iconRetinaUrl: markerIcon2x,
        iconUrl: markerIcon,
        shadowUrl: markerShadow
    });

    L.EditToolbar.Delete.include({
        removeAllLayers: false
    });

    map.addLayer(positionPolygonLayers);
    map.addLayer(polygonLayers);
    map.addLayer(markerLayers);
    map.addLayer(pumpingLayers);

    map.on("zoomend", mapZoomHandler);
    map.on("draw:deleted", deleteShapeHandler);

    L.tileLayer
        .wms("https://opengeodata.goteborg.se/services/ortofoto/wms/v1", {
            layers: "orto_2023",
            maxNativeZoom: 19,
            maxZoom: 19,
            minZoom: 13
        })
        .addTo(map);

    if (shouldImportDraw) {
        importAndSetupOptionsController();
        setupDrawControls();
    }

    const asyncFetch = async () => {
        await fetchPolygons();
        await fetchPermits();
        await fetchPumpings();
    };

    asyncFetch();

    var connection = new signalR.HubConnectionBuilder().withUrl("/notifications").build();
    connection.on("ReceiveNotification", async (message) => {
        if (message === "PumpingsUpdated") {
            await fetchPumpings();
        } else if (message === "WorkPermitsUpdated") {
            await fetchPermits();
        }
    });
    connection.start();

    const filterForm = document.getElementById("filter-form");
    filterForm.onsubmit = (event) => {
        event.preventDefault();
        fetchPermits();
    };

    async function fetchPermits() {
        let permits = await (await fetch("/Map?" + new URLSearchParams(new FormData(filterForm)))).json();

        addPermitMarkers(permits);
        addPermitPositionsPolygons(permits);
    }

    function addPermitPositionsPolygons(permits) {
        positionPolygonLayers.clearLayers();
        for (let permit of permits) {
            for (let position of permit.positions) {
                if (position.isActive && position.polygonJsonObject) {
                    let parsedObj = JSON.parse(position.polygonJsonObject);

                    let type = parsedObj.type;

                    let shape;
                    if (type === "Polygon") {
                        shape = L.polygon(parsedObj.latlngs, parsedObj.options);
                    } else if (type === "LineString") {
                        shape = L.polyline(parsedObj.latlngs, parsedObj.options);
                    } else {
                        shape = L.circle(parsedObj.latlngs, parsedObj.radius, parsedObj.options);
                    }

                    const popupContent = getPermitPopupContent(permit, position);

                    shape.bindPopup(popupContent);

                    positionPolygonLayers.addLayer(shape);
                }
            }
        }
    }

    async function fetchPolygons() {
        let polygons = await (await fetch("/Map?handler=Polygons")).json();

        polygonLayers.clearLayers();

        for (let polygon of polygons) {
            let parsedObj = JSON.parse(polygon.body);
            let type = parsedObj.type;
            let shape;
            if (type === "Polygon") {
                shape = L.polygon(parsedObj.latlngs, parsedObj.options);
            } else if (type === "LineString") {
                shape = L.polyline(parsedObj.latlngs, parsedObj.options);
            } else if (type === "Point" && parsedObj.options.icontype) {
                let iconRadioButton = document.querySelector(
                    '[name="selected-marker-icon"][value="' + parsedObj.options.icontype + '"]'
                );
                if (iconRadioButton == null) iconRadioButton = document.querySelector('[name="selected-marker-icon"]');
                const iconUrl = iconRadioButton.parentElement.querySelector("img").src;
                const markerIcon = L.divIcon({
                    className: "custom-div-icon",
                    html: `<div class='border-black marker-pin'></div>
                           <div class='marker-pin-overlay'></div>
                           <img class="awesome" style="z-index: 1000000;" src="${iconUrl}">`,
                    iconSize: [30, 42],
                    iconAnchor: [15, 42],
                    popupAnchor: [0, -35]
                });
                shape = L.marker(parsedObj.latlngs, { icon: markerIcon });
            } else {
                shape = L.circle(parsedObj.latlngs, parsedObj.radius, parsedObj.options);
            }
            shape.id = parsedObj.id;

            if (polygon.comment) shape.bindPopup(polygon.comment);

            polygonLayers.addLayer(shape);
        }
    }

    async function fetchPumpings() {
        const eventData = await (await fetch("/Map?handler=Pumpings")).json();
        const pumpings = eventData.pumpings;
        const lastEventDateTime = eventData.lastEventDateTime;

        const el = document.getElementById("NewPipeLastEventDateTime");
        if (el) {
            if (lastEventDateTime) {
                el.innerHTML = lastEventDateTime;
                el.parentElement.classList.remove("d-none");
            } else {
                el.innerHTML = "";
                el.parentElement.classList.add("d-none");
            }
        }

        pumpingLayers.clearLayers();

        for (let pumping of pumpings) {
            const points = pumping.routePoints.map((rp) => new L.LatLng(rp.latitude, rp.longitude));
            const pumpingLine = new L.Polyline(points, {
                color: pumping.color,
                weight: 4,
                opacity: 0.5,
                smoothFactor: 1
            });

            pumpingLayers.addLayer(pumpingLine);
        }
    }

    function addPermitMarkers(permits) {
        markerLayers.clearLayers();

        for (let permit of permits) {
            const permitName = permit.permitName;

            let permitCategory;

            if (permit.permitName === PermitType.EnergyHarbourPermit && permit.isColdWorkOnly) {
                permitCategory = PermitCategory.ColdWork;
            } else if (Array.isArray(permit.permitCategories) && permit.permitCategories.length > 0) {
                permitCategory = getHighestPermitCategory(permit.permitCategories);
            } else {
                console.error("No permit category was present on the permit when rendering", permit);
                permitCategory = PermitCategory.Other;
            }

            for (let position of permit.positions) {
                const category = position.category ?? permitCategory;

                const { markerSrc } = getPermitIcon(
                    permitName,
                    category,
                    permit.status,
                    position.isActive,
                    permit.maritimeEventType
                );

                const icon = L.icon({
                    iconUrl: markerSrc,
                    iconSize: [30, 42],
                    iconAnchor: [15, 42],
                    popupAnchor: [0, -35]
                });

                const popupContent = getPermitPopupContent(permit, position);

                const zIndex = getMarkerZIndex(permit, position);
                let marker = L.marker([position.latitude, position.longitude], { icon: icon })
                    .bindPopup(popupContent)
                    .setZIndexOffset(zIndex);

                markerLayers.addLayer(marker);
            }
        }
    }

    function importAndSetupOptionsController() {
        import("./controls/polygon-options-control").then(({ default: optionsController }) => {
            optionsController.create().addTo(map);
        });
    }

    function setupDrawControls() {
        let options = {
            position: "topright",
            draw: {
                polyline: {
                    shapeOptions: {
                        color: "#f357a1",
                        weight: 3
                    }
                },
                polygon: {
                    shapeOptions: {
                        weight: 2,
                        color: "#bada55"
                    }
                },
                circle: true,
                rectangle: {
                    shapeOptions: {
                        clickable: false,
                        weight: 2
                    }
                },
                marker: true,
                circlemarker: false
            },
            edit: {
                featureGroup: polygonLayers
            }
        };

        let drawControl = new L.Control.Draw(options);
        map.addControl(drawControl);

        map.on(L.Draw.Event.CREATED, async function (e) {
            let layer = e.layer;

            let selectedColor = document.getElementById("selected-polygon-color").value;
            let comment = document.getElementById("polygon-comment-textarea").value;
            let selectedIconType = document.querySelector('[name="selected-marker-icon"]:checked').value;

            if (selectedColor) layer.options.color = selectedColor;

            if (comment) layer.bindPopup(comment);

            if (e.layerType === "marker") layer.options.icontype = selectedIconType;

            let geoJson = layer.toGeoJSON();

            polygonLayers.addLayer(layer);

            layer.openPopup();

            let type = geoJson.geometry.type;
            let storedShape = {
                type: geoJson.geometry.type,
                options: layer.options
            };

            if (type === "Polygon") {
                storedShape.latlngs = layer._latlngs[0].map((p) => [p.lat, p.lng]);
            } else if (type === "LineString") {
                storedShape.latlngs = layer._latlngs.map((p) => [p.lat, p.lng]);
            } else {
                storedShape.latlngs = [layer._latlng.lat, layer._latlng.lng];
                storedShape.radius = layer._mRadius;
            }

            let leafletPolygon = {
                Body: JSON.stringify(storedShape),
                comment: comment,
                icontype: layer.options.icontype
            };

            const response = await fetch("/Map", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    RequestVerificationToken: document.getElementsByName("__RequestVerificationToken")[0].value
                },
                body: JSON.stringify(leafletPolygon)
            });

            const responseJson = await response.json();
            layer.id = responseJson.id;
        });
    }

    function mapZoomHandler() {
        let zoomlevel = map.getZoom();
        if (zoomlevel < 12) {
            map.removeLayer(polygonLayers);
            map.removeLayer(markerLayers);
        }
        if (zoomlevel >= 12) {
            map.addLayer(polygonLayers);
            map.addLayer(markerLayers);
        }
    }

    function deleteShapeHandler(event) {
        const deletedShapeIds = Object.values(event.layers._layers).map((shape) => shape.id);

        fetch("/Map?handler=RemovePolygons", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                RequestVerificationToken: document.getElementsByName("__RequestVerificationToken")[0].value
            },
            body: JSON.stringify(deletedShapeIds)
        });
    }
}

function getPermitPopupContent(permit, position) {
    const contactPerson = permit.workPermitTags.find((tag) => tag.type === "ContactPerson");
    const workLeaderPhoneNumber = permit.workPermitTags.find((tag) => tag.type === "WorkLeaderPhoneNumber");
    const shipName = permit.workPermitTags.find((tag) => tag.type === "ShipName");
    let popupContent;

    if (!permit.hasReducedInfo) {
        popupContent = `<ul style="list-style: none; padding: 0; margin: 0; width: 18rem">
                            <li>
                                <a href="/permits/${permit.permitName}${permit.permitName === PermitType.GeneralDepartmentPermit ? `/${permit.departmentId}` : ""}?documentReferenceId=${permit.documentReferenceId}">
                                    ${permit.displaySequenceId}
                                </a>    
                            </li>
                            <li>
                                <b>Start:</b> ${new Date(
                                    permit.startDate
                                ).toLocaleDateString()} <b>Slut:</b> ${new Date(permit.endDate).toLocaleDateString()}
                            </li>
                            <li>
                                <b>Status:</b> ${permit.displayStatus}
                            </li>
                            ${
                                permit.entrepreneur
                                    ? `
                            <li>
                                <b>Entreprenör:</b> ${permit.entrepreneur}
                            </li>`
                                    : ""
                            }
                            ${
                                !permit.isAnonymousPermit
                                    ? `
                            <li>
                                <b>Företag:</b> ${permit.companyNameSnapshot}
                            </li>`
                                    : ""
                            }
                            ${
                                contactPerson
                                    ? `
                            <li>
                                <b>Kontaktperson:</b> ${contactPerson.value}
                            </li>`
                                    : ""
                            }
                            ${
                                workLeaderPhoneNumber
                                    ? `
                            <li>
                                <b>Telefonnummer arbetsledare:</b> <a href="tel:${workLeaderPhoneNumber.value}">${workLeaderPhoneNumber.value}</a>
                            </li>`
                                    : ""
                            }
                            ${
                                shipName
                                    ? `
                            <li>
                                <b>Fartygets namn:</b> ${shipName.value}
                            </li>`
                                    : ""
                            }
                            ${
                                !permit.isAnonymousPermit && permit.userPhoneNumber
                                    ? `
                            <li>
                                <b>Telefonnummer tillståndshavare:</b> <a href="tel:${permit.userPhoneNumber}">${permit.userPhoneNumber}</a>
                            </li>`
                                    : ""
                            }
                           ${
                               position.comment
                                   ? `
                           <li>
                                <b>Plats</b> ${position.comment}
                            </li>`
                                   : ``
                           }
                            <li>
                                <b>Arbetsbeskrivning:</b> <br />
                                ${permit.description}
                            </li>
                        </ul>`;
    } else {
        popupContent = `<div>
                                ${permit.displaySequenceId}
                        </div>`;
    }

    const detailedInfo = !permit.hasReducedInfo
        ? `
            <li>
                <b>
                    <a href="/permits/${permit.permitName}?documentReferenceId=${
                        permit.documentReferenceId
                    }">Mer information</a>
                </b>
            </li>
            <li>
                <b>Start:</b> ${new Date(permit.startDate).toLocaleDateString()} <b>Slut:</b> ${new Date(
                    permit.endDate
                ).toLocaleDateString()}
                </li>
            <li>
                <b>Status:</b> ${permit.displayStatus}
            </li>
                ${permit.entrepreneur ? `<li><b>Entreprenör:</b> ${permit.entrepreneur}</li>` : ""} 
            <li>
                <b>Företag:</b> ${permit.companyNameSnapshot}
            </li>
            ${
                contactPerson
                    ? `<li>
                    <b>Kontaktperson:</b> ${contactPerson.value}
                </li>`
                    : ""
            }
            ${
                workLeaderPhoneNumber
                    ? `<li>
                    <b>Telefonnummer arbetsledare:</b> ${workLeaderPhoneNumber.value}
                </li>`
                    : ""
            }
            ${
                permit.userPhoneNumber
                    ? `
                                <li>
                        <b>Telefonnummer tillståndshavare:</b> ${permit.userPhoneNumber}
                </li>            
                `
                    : ""
            }
           ${
               position.comment
                   ? `<li>
                    <b>Plats</b> ${position.comment}
                </li>`
                   : ``
           }

            <li>
                <b>Arbetsbeskrivning:</b> <br/>
                ${permit.description}
            </li>
         
        `
        : ``;

    return popupContent;
}
